import {
  internalAgSystemsTheme,
  MUI_AUTOCOMPLETE_VALUE_WARNING_REGEX,
  sentryBeforeSend,
  suppressConsole,
} from '@australiangreens/ag-internal-components';
import { ThemeProvider } from '@emotion/react';
import { StyledEngineProvider } from '@mui/material';
import * as Sentry from '@sentry/react';
import React from 'react';
import { createRoot } from 'react-dom/client';

import RouterErrorFallback from '@components/RouterErrorFallback';
import TopLevelBlockingErrorBoundary from '@components/TopLevelBlockingErrorBoundary';
import { createBrowserRouter, RouterProvider } from 'react-router';
import App from './App';
import * as allConfig from './config';
import {
  environment,
  frontendCommitHash,
  frontendVersion,
  isAxeCoreDisabled,
  isSentryDisabled,
  microfrontendURLPrefix,
  sentryDsn,
  sentryFeedbackConfig,
  spaMode,
} from './config';
import { ConfigError } from './errors';
import './index.css';
import { logger } from './util';

declare global {
  interface Window {
    listmanagerFrontendJsRender: unknown;
    listmanagerFrontendJsUnmount: unknown;
  }
}

logger.info('>>>>>>>>>>>>>>>STARTUP<<<<<<<<<<<<<<<');
logger.info(`environment = ${environment}`);
logger.info(`spaMode = ${spaMode}`);
logger.info(`microfrontendURLPrefix = ${microfrontendURLPrefix}`);
logger.info(`Frontend version = ${frontendVersion}`);
logger.info(`Frontend commit hash = ${frontendCommitHash}`);
logger.info(`isSentryDisabled = ${isSentryDisabled}`);
logger.info(`isAxeCoreDisabled = ${isAxeCoreDisabled}`);
logger.debug('config', allConfig);

if (!isSentryDisabled) {
  // If sentryDsn is not set, the sdk just won't do anything
  if (sentryDsn) logger.debug('Start initialising Sentry...');
  Sentry.init({
    dsn: sentryDsn,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.feedbackIntegration(sentryFeedbackConfig),
    ],
    environment,
    tracesSampleRate: environment === 'prod' ? 0.1 : 0,
    beforeSend: sentryBeforeSend,
  });

  if (sentryDsn) logger.debug('...Finished initialising Sentry');
}

// [LIST-773] Supress a harmless warning caused by the way we use Autocomplete
// in SingleAutocomplete. MUI doesn't output it in production and in tests we
// need to be able to spy on the original console functions in some cases, so
// can't globally change them
if (!['prod', 'test'].includes(environment)) {
  suppressConsole('warn', [MUI_AUTOCOMPLETE_VALUE_WARNING_REGEX]);
}

export const reportAccessibility = async (
  Applic: typeof React,
  configdata?: Record<string, unknown>
): Promise<void> => {
  if (environment !== 'prod' && !isAxeCoreDisabled) {
    const axe = await import('@axe-core/react');
    const ReactDOMUS = await import('react-dom');

    // const diagnosticLogger = (results: Parameters<typeof axe.logToConsole>[0]) => {
    //   const ariaViolations = results.violations.filter((v) => v.id === 'aria-required-children');
    //   ariaViolations.forEach((v) => {
    //     v.nodes.forEach((node) => {
    //       console.warn('aria-required-children:', node.html, node.target, node.failureSummary);
    //     });
    //   });
    //   axe.logToConsole(results);
    // };

    await axe.default(
      Applic,
      ReactDOMUS,
      1000,
      configdata ?? {},
      undefined /*, diagnosticLogger */
    );
  }
};

export default reportAccessibility;

function renderApp(containerId: string) {
  const router = createBrowserRouter([
    { path: '*', Component: App, errorElement: <RouterErrorFallback /> },
  ]);

  const root = createRoot(document.getElementById(containerId)!);
  root.render(
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={internalAgSystemsTheme}>
        <TopLevelBlockingErrorBoundary sentryEnabled={!isSentryDisabled}>
          <RouterProvider router={router} />
        </TopLevelBlockingErrorBoundary>
      </ThemeProvider>
    </StyledEngineProvider>
  );
}

if (spaMode === 'microfrontend') {
  // This global function is the primary micro frontend contract with the
  // container application
  // TODO: should this be defined in .env instead? I don't think so...?
  window.listmanagerFrontendJsRender = renderApp;
  // window.listmanagerFrontendJsUnmount = unmountApp;
} else if (spaMode === 'standalone') {
  renderApp('root');
} else {
  throw new ConfigError(
    "config.spaMode is set incorrectly. Can only be 'microfrontend' or 'standalone'"
  );
}

setTimeout(async () => await reportAccessibility(React), 10000);
